import React from 'react';
import { CalendarDaysIcon, ClockIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const SessionList = ({ sessions, selectedSessionsList, onSessionSelect }) => {
  const getSessionStatus = (session) => {
    if (session.session_type === 'scheduled') return 'Scheduled';
    if (session.session_type === 'unscheduled') return 'Unscheduled';
    return 'Unknown';
  };

  const getSessionStatusColor = (session) => {
    if (session.session_type === 'scheduled') return 'bg-green-100 text-green-800';
    if (session.session_type === 'unscheduled') return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getCrewDisplayInfo = (session) => {
    if (!session.crew_assignments || session.crew_assignments.length === 0) {
      return { count: 0, names: [], hasCrews: false };
    }
    const crewNames = session.crew_assignments.map((assignment) =>
      assignment.crew?.name ? assignment.crew.name : `Crew ${assignment.crew_id}`
    );
    return { count: session.crew_assignments.length, names: crewNames, hasCrews: true };
  };

  const getSessionRelationshipInfo = (session) => {
    if (session.parent_session_id) {
      return { type: 'child', parentId: session.parent_session_id };
    }
    if (session.child_sessions && session.child_sessions.length > 0) {
      return { type: 'parent', childCount: session.child_sessions.length };
    }
    return { type: 'standalone' };
  };

  if (sessions.length === 0) {
    return (
      <div className="p-6 text-center">
        <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No sessions found</h3>
        <p className="mt-1 text-sm text-gray-500">
          No training sessions found for the selected date range and criteria.
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {sessions.map((session) => {
        const crewInfo = getCrewDisplayInfo(session);
        const relationshipInfo = getSessionRelationshipInfo(session);
        const isSelected = selectedSessionsList.includes(session.id);

        return (
          <div
            key={session.id}
            className={`cursor-pointer px-6 py-4 hover:bg-gray-50 ${
              isSelected ? 'border-l-4 border-blue-400 bg-blue-50' : ''
            }`}
            onClick={() => onSessionSelect(session.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CalendarDaysIcon className="h-8 w-8 text-gray-400" />
                </div>
                <div className="ml-4 flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="text-sm font-medium text-gray-900">{session.title}</h4>
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${getSessionStatusColor(
                        session
                      )}`}
                    >
                      {getSessionStatus(session)}
                    </span>
                    {relationshipInfo.type === 'parent' && (
                      <span className="rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800">
                        Parent ({relationshipInfo.childCount} children)
                      </span>
                    )}
                    {relationshipInfo.type === 'child' && (
                      <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800">
                        Child Session
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <CalendarDaysIcon className="mr-1 h-4 w-4" />
                      {format(new Date(session.date), 'MMM dd, yyyy')}
                    </span>
                    {session.default_start_time && (
                      <span className="flex items-center">
                        <ClockIcon className="mr-1 h-4 w-4" />
                        {session.default_start_time}
                      </span>
                    )}
                    {session.duration && <span>{session.duration} minutes</span>}
                  </div>
                  {session.description && (
                    <p className="mt-1 text-sm text-gray-600">{session.description}</p>
                  )}
                  {crewInfo.hasCrews ? (
                    <div className="mt-2 flex items-center space-x-2">
                      <UserGroupIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {crewInfo.count} crew{crewInfo.count !== 1 ? 's' : ''}:{' '}
                        {crewInfo.names.join(', ')}
                      </span>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-center space-x-2">
                      <UserGroupIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-500">No crews assigned</span>
                    </div>
                  )}
                </div>
              </div>
              {isSelected && (
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SessionList;
